import './Sidebar.scss'
import Drawer from 'devextreme-react/drawer'
import { Template } from 'devextreme-react/core/template'
import { useSidebar } from '../context/SidebarContext'
import { useLocation, useNavigate } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../../../features/auth/context/authContext'
import { routes } from '../../../navigation/appRoutes'
import { classNames } from '../../../helpers/cssClassHelper'
import { DropdownOverlay } from '../../../components'
import { DropdownItem } from '../../../components/dropdown/DropdownOverlay'

interface SidebarItem {
    text: string
    icon: React.ReactNode
    path: string
}

export default function Sidebar() {
    const sidebar = useSidebar()
    const navigate = useNavigate()
    const location = useLocation()
    const { t } = useTranslation();
    const { isLoggedIn, hasAnyRole } = useAuth()

    const [menuItems, setMenuItems] = useState<SidebarItem[] | null>(null)

    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth <= 600 && sidebar.isSidebarExpanded) {
                sidebar.hide()
            }
        }
        window.addEventListener('resize', handleResize)
        return () => window.removeEventListener('resize', handleResize)
    }, [sidebar])

    useEffect(() => {
        const loadMenuItems = async () => {
            const filteredRoutes = await Promise.all(
                routes
                    .filter(route => !!route.sidebarItem)
                    .map(async route => {
                        if (!route.allowedRoles) return route
                        const allowed = await hasAnyRole(...route.allowedRoles)
                        return allowed ? route : null
                    })
            )

            const sidebarItems = filteredRoutes
                .filter((r): r is typeof routes[number] => r !== null)
                .map(route => ({
                    text: t(route.sidebarItem!.text),
                    icon: route.sidebarItem!.icon,
                    path: route.path
                }))

            setMenuItems(sidebarItems)
        }

        loadMenuItems()
    }, [hasAnyRole, t])

    if (!isLoggedIn || menuItems === null) {
        return null
    }

    const mapNavigationItemsToDropdownItems = (items: SidebarItem[]): DropdownItem[] => {
        return items.map(item => ({
            text: item.text,
            icon: item.icon,
            onClick: () => {
                navigate(item.path)
                sidebar.toggleSidebar()
            }
        }))
    }

    const menuItemText = classNames(
        "font-lato",
        "menu-item-text",
        sidebar.isSidebarExpanded ? "show" : "hide"
    )

    return (
        <div className='sidebar'>
            {sidebar.isXSmall && sidebar.isSidebarExpanded &&
                <DropdownOverlay
                    onClick={sidebar.toggleSidebar}
                    items={mapNavigationItemsToDropdownItems(menuItems)} /> }

            {!sidebar.isXSmall &&
                <Drawer
                    className='drawer menu'
                    position={'before'}
                    minSize={40}
                    maxSize={220}
                    revealMode='expand'
                    opened={sidebar.isSidebarExpanded}
                    template={'menu'}>
                    <Template name={'menu'}>
                        {menuItems.map((item, index) => (
                            <div key={index} className='menu-item-container'>
                                <a href={item.path}
                                    className={`menu-item ${location.pathname.includes(item.path.split("?")[0]) ? 'selected' : ''}`}
                                    onClick={e => {
                                        e.preventDefault()
                                        navigate(item.path)
                                    }}>
                                    <div style={{ marginRight: "4px" }}>
                                        {item.icon}
                                    </div>
                                    <span className={menuItemText}>{item.text}</span>
                                </a>
                            </div>
                        ))}
                    </Template>
                </Drawer>
            }
        </div>
    )
}
